import { useState } from 'react';
import type { DocImage } from '../../utils/source-images';

/**
 * The pictures belonging to a retrieved passage.
 *
 * The model reads text only, so these are not what it matched on — they are what
 * the READER needs to recognise the product the passage is about. They come free
 * with the chunk's metadata, which is why showing them needs no re-index.
 *
 * Origin URLs fail routinely (moved, hotlink-blocked, offline), so a broken one
 * removes itself rather than leaving a torn-image icon in the results.
 */
const ChunkImages = ({ images }: { images: DocImage[] }) => {
  const [broken, setBroken] = useState<Record<string, true>>({});
  const shown = images.filter((i) => !broken[i.src]);
  // Silence here reads as "this product has no pictures", which is a different
  // and wrong answer. Tallman's origin returns 403 to anything that is not a
  // browser session on its own site, so for that corpus EVERY url is blocked —
  // say so once, quietly, instead of rendering nothing.
  if (!shown.length) {
    return images.length ? (
      <p className="mt-2 text-[11px] text-ink-muted">
        {images.length} image{images.length === 1 ? '' : 's'} on this document, blocked by the
        origin. Storing the bytes at import is what makes them viewable.
      </p>
    ) : null;
  }
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {shown.map((img) => (
        <a
          key={img.src} href={img.src} target="_blank" rel="noreferrer noopener"
          title={img.alt || img.caption || 'Open the full-size image'}
          className="ui-press ui-inset flex h-20 w-20 items-center justify-center overflow-hidden bg-white dark:bg-gray-900"
        >
          <img
            src={img.src} alt={img.alt || ''} loading="lazy"
            onError={() => setBroken((b) => ({ ...b, [img.src]: true }))}
            className="max-h-full max-w-full object-contain"
          />
        </a>
      ))}
    </div>
  );
};

export default ChunkImages;
